import {isTealiumEnabled} from './isTealiumEnabled';
import {enqueueTealiumEvent, isTealiumReady} from './tealiumInit';
import {buildBaseDataLayer} from './buildBaseDataLayer';
import type {TealiumData} from './types';

/**
 * Wrapper utag.view / utag.link conforme au schéma TRACE+ (doc Vodafone).
 *
 * Tous les payloads incluent automatiquement le data layer de base
 * (page_name, page_section, page_country, page_locale, page_platform,
 *  visitor_id_asset_active, visitor_login_status, site_version, site_type,
 *  page_parent_domain). Cf. buildBaseDataLayer / tealiumContext.
 */

// `kind` is not computed from any condition — every caller passes a fixed
// literal ('view' or 'link') decided at the call site. This is the only
// place that value is actually used to pick the real Tealium API call.
const send = (
  kind: 'view' | 'link',
  payload: TealiumData,
): void => {
  if (!isTealiumEnabled()) return;

  const enrichedPayload: TealiumData = {
    tealium_event: kind,
    ...payload,
  };

  if (typeof window !== 'undefined') {
    window.utag_data = {...(window.utag_data || {}), ...enrichedPayload};
  }

  if (isTealiumReady()) {
    try {
      // The actual utag.view vs utag.link decision, driven purely by `kind`.
      if (kind === 'view') window.utag?.view(enrichedPayload);
      else window.utag?.link(enrichedPayload);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[tealium]', kind, 'failed', err);
    }
    return;
  }

  enqueueTealiumEvent({type: kind, data: enrichedPayload});
};

export interface TealiumPageViewProps {
  path: string;
  title?: string;
  pageSection?: string;
  pageType?: string;
  /** Surcharges optionnelles (ex. champs product_*, transaction_* sur subscription view) */
  extra?: TealiumData;
}

export interface TealiumAuthProps {
  /** User id Trace, propagé en visitor_id_asset_active */
  userId?: string;
  /** Date d'inscription au format <created date> */
  registrationDate?: string;
  accountType?: 'email' | 'facebook' | 'google' | 'apple' | string;
}

export interface TealiumSubscriptionCompletedProps {
  /** subscription-id renvoyé par Penrose sur le redirect (result=active) */
  subscriptionId?: string;
  pageSection?: string;
}

// Every tracker call (auth, subscription, page view) funnels through view()/link().
// Wrapping here — rather than at each of the ~9 call sites across the app — guarantees
// a Tealium bug can never interrupt the real business logic (login, signup, checkout)
// that these calls are dropped into.
// Sends `eventName` as the explicit tealium_event, on whichever utag.* call
// `kind` picks. Used by link() as-is, and by auth.loginSuccessful /
// subscription.completed to fire a named business event via utag.view.
// NOT used by the plain view() below — that one leaves tealium_event to
// default to 'view' inside send(), which this function deliberately overrides.
const track = (
  kind: 'view' | 'link',
  eventName: string,
  props: TealiumPageViewProps,
): void => {
  try {
    const base = buildBaseDataLayer({
      path: props.path,
      title: props.title,
      pageSection: props.pageSection,
      pageType: props.pageType,
      eventName,
    });
    send(kind, {
      ...base,
      tealium_event: eventName,
      ...(props.extra || {}),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[tealium] ${kind} failed`, err);
  }
};

// Plain route-change page view: tealium_event is intentionally left
// unset here so it defaults to 'view' in send() — do not route this
// through track(), which would force tealium_event to 'page_view'.
const view = (props: TealiumPageViewProps): void => {
  try {
    const base = buildBaseDataLayer({
      path: props.path,
      title: props.title,
      pageSection: props.pageSection,
      pageType: props.pageType,
      eventName: 'page_view',
    });
    send('view', {
      ...base,
      ...(props.extra || {}),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[tealium] view failed', err);
  }
};

const link = (eventName: string, props: TealiumPageViewProps): void =>
  track('link', eventName, props);

export const tealiumTracker = {
  /** utag.view standard, attendu sur chaque changement de route */
  view,
  /** utag.link générique, à utiliser quand aucun helper n'existe */
  link,

  auth: {
    // Fires as utag.view (not utag.link) per Vodacom QA data-layer validation
    // (2026-09-11): login/subscription completion events must populate under
    // utag.view. tealium_event stays 'login_successful'.
    loginSuccessful: (
      pagePath: string,
      props: TealiumAuthProps & {pageSection?: string} = {},
    ) =>
      track('view', 'login_successful', {
        path: pagePath,
        pageSection: props.pageSection,
        extra: {
          event_name: 'login',
          ...(props.userId ? {visitor_id_asset_active: props.userId} : {}),
          ...(props.accountType ? {visitor_login_type: props.accountType} : {}),
        },
      }),
    signupSuccessful: (
      pagePath: string,
      props: TealiumAuthProps & {pageSection?: string} = {},
    ) =>
      link('signup_successful', {
        path: pagePath,
        pageSection: props.pageSection,
        extra: {
          event_name: 'registration',
          ...(props.userId ? {visitor_id_asset_active: props.userId} : {}),
          ...(props.accountType ? {visitor_login_type: props.accountType} : {}),
          ...(props.registrationDate
            ? {registration_date: props.registrationDate}
            : {}),
        },
      }),
    signout: (pagePath: string, pageSection?: string) =>
      link('signout', {
        path: pagePath,
        pageSection,
        extra: {event_name: 'signout'},
      }),
  },

  subscription: {
    /**
     * Fired on the content page once Penrose redirects back with
     * result=active (see useVodacomRedirect). Trace+ currently sells a
     * single fixed daily plan through the Vodacom/Penrose flow, and Penrose
     * confirmed (2026-08-29) these product/plan fields can be hardcoded
     * rather than passed dynamically in the redirect — only subscriptionId
     * (their subscription-id) varies per purchase. Update this if a second
     * plan ships or Penrose starts sending these values instead.
     */
    completed: (
      pagePath: string,
      props: TealiumSubscriptionCompletedProps = {},
    ): void => {
      try {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const toDateString = (d: Date): string => d.toISOString().split('T')[0];

        // Fires as utag.view (not utag.link) per Vodacom QA data-layer
        // validation (2026-09-11) — see note on auth.loginSuccessful above.
        track('view', 'subscription_completed', {
          path: pagePath,
          pageSection: props.pageSection,
          extra: {
            event_name: 'subscription',
            product_id: 500,
            product_name: 'Trace Plus',
            product_price: 'R7',
            product_category: 'entertainment',
            product_sku: 'trace-daily-01',
            product_type: 'digital-content',
            subscription_type: 'daily',
            visitor_asset_plan_name_active: 'Trace Plus Daily',
            visitor_asset_plan_type_active: 'Daily',
            visitor_asset_plan_brand_active: 'Trace',
            visitor_asset_duration_active: '1 day',
            visitor_asset_type_active: 'digital-content',
            visitor_assest_date_start_active: toDateString(today),
            visitor_assest_date_end_active: toDateString(tomorrow),
            ...(props.subscriptionId
              ? {
                  visitor_asset_plan_id_active: props.subscriptionId,
                  visitor_asset_subscription_id_active: props.subscriptionId,
                  transaction_id: props.subscriptionId,
                }
              : {}),
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[tealium] subscription.completed failed', err);
      }
    },
  },
};

export type TealiumTracker = typeof tealiumTracker;
